import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, NgZone, Input, Output, EventEmitter } from '@angular/core';
import { NgbDateStruct, NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { MediaMatcher, BreakpointObserver } from '@angular/cdk/layout';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date';
import { VisitorService } from '../../../services/visitor.service';
import { isNullOrUndefined } from 'util';
import { StoreSelection } from '../../../models/store';
import { ObserveOnMessage } from 'rxjs/internal/operators/observeOn';
import { of } from 'rxjs';

// Breakpoint
const isLargeScreenMediaQuery: string = '(min-width: 500px)';

const equals = (one: NgbDateStruct, two: NgbDateStruct) =>
  one && two && two.year === one.year && two.month === one.month && two.day === one.day;

const before = (one: NgbDateStruct, two: NgbDateStruct) =>
  !one || !two ? false : one.year === two.year ? one.month === two.month ? one.day === two.day
    ? false : one.day < two.day : one.month < two.month : one.year < two.year;

const after = (one: NgbDateStruct, two: NgbDateStruct) =>
  !one || !two ? false : one.year === two.year ? one.month === two.month ? one.day === two.day
    ? false : one.day > two.day : one.month > two.month : one.year > two.year;

export interface DatepickerUpdate {
  toDate: Date;
  fromDate: Date;
}

@Component({
  selector: 'app-compare',
  templateUrl: './compare.component.html',
  styleUrls: ['./compare.component.scss']
})
export class CompareComponent implements OnInit, OnDestroy {
  /* Calendar Settings */
  hoveredDate: NgbDateStruct;

  fromDate: NgbDateStruct;
  toDate: NgbDateStruct;

  fromDate2: NgbDateStruct;
  toDate2: NgbDateStruct;

  maxDate: NgbDateStruct;
  minDate: NgbDateStruct;

  isSelectingRange2: boolean = false;
  rangeLength: number;

  @Input()
  minYear = 2016;

  @Input()
  selectedStore: StoreSelection;

  @Output()
  dateSelected = new EventEmitter<DatepickerUpdate>();

  @Output()
  isSmallDatepicker = new EventEmitter<Boolean>();

  // Large and Small calendars start on different months
  startDateSmall: NgbDateStruct;
  startDateLarge: NgbDateStruct; // startDateSmall minus 1 month

  /* Resize Calendar dynamically */
  show: boolean = true;
  matcher: MediaQueryList;
  resizeListener: any;
  currentCalendar: TemplateRef<any>|null = null;

  @Output()
  isUpdating = new EventEmitter<boolean>();

  @ViewChild('largeCalendar')
  largeCalendar: TemplateRef<any>|null = null;
  @ViewChild('smallCalendar')
  smallCalendar: TemplateRef<any>|null = null;

  constructor(private calendar: NgbCalendar,
              private breakpointObserver: BreakpointObserver,
              private mediaMatcher: MediaMatcher,
              private zone: NgZone,
              private _visitorService: VisitorService) {

   // Max date today - events can only exist up until today
   this.maxDate = this.calendar.getToday();
   // Minimum date hardcoded to 2016
   // In future - set min date to the first event of the selected store's history
   this.minDate = new NgbDate(this.minYear, 1, 1)

   // Initially select last 14 days starting today
   this.fromDate = this.calendar.getToday();
   this.toDate = this.calendar.getToday();

   this.startDateSmall = new NgbDate(this.toDate.year, this.toDate.month, 1);
   this.startDateLarge = new NgbDate(this.toDate.year, this.toDate.month - 1, 1); // startDateSmall minus 1 month
  }

  reset() {
    this.fromDate = this.fromDate2 = this.toDate = this.toDate2 = null;
  }

  update() {
    this.updateVisitorServiceDates().subscribe((response:any) => {
      this.isUpdating.emit(false);
    }, (error: any) => {
      console.error(error);
      this.isUpdating.emit(false);
    }); 
  }

  getNumDaysBetweenDates(date1: Date, date2: Date) {
    // The number of milliseconds in one day
    let ONE_DAY = 1000 * 60 * 60 * 24

    // Convert both dates to milliseconds
    let date1_ms = date1.getTime()
    let date2_ms = date2.getTime()

    // Calculate the difference in milliseconds
    let difference_ms = Math.abs(date1_ms - date2_ms)

    // Convert back to days and return
    return Math.round(difference_ms/ONE_DAY)
  }

  onDateSelection(date: NgbDateStruct) {

    if(this.isSelectingRange2) {
      // Set dates for second range
      this.fromDate2 = this.subtractDaysFromDate(this.nearestDate, this.rangeLength);
      this.toDate2 = this.nearestDate;

      // Update bar graph
      this.isUpdating.emit(true);
      this.updateVisitorServiceDates().subscribe((response:any) => {
        this.isUpdating.emit(false);
      }, (error: any) => {
        console.error(error);
        this.isUpdating.emit(false);
      }); 
      // this.updateVisitorServiceDates();
      this.isSelectingRange2 = false;
      return;
    }

    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } 

    else if (this.fromDate && !this.toDate && after(date, this.fromDate)) { 
      this.toDate = date;
      this.rangeLength = this.getNumDaysBetweenDates(this.convertNgbDateStructToDate(this.toDate), this.convertNgbDateStructToDate(this.fromDate));
      this.isSelectingRange2 = true;
    } 

    else {
      this.toDate = null;
      this.fromDate = date;
      // Reset second range
      this.fromDate2 = null;
      this.toDate2 = null;
    }
  }

  convertNgbDateStructToDate(date: NgbDateStruct): Date {
    return new Date(date.year, date.month-1, date.day);
  }

  convertDateToNgbDateStruct(date: Date): NgbDateStruct {
    return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  }

  updateVisitorServiceDates() {
    if (this.fromDate == null || this.toDate == null || this.fromDate2 == null || this.toDate2 == null)
      return of(null);

    // Update bar graph
    return this._visitorService.setDayCompareDateSelection(
      this.convertNgbDateStructToDate(this.fromDate), 
      this.convertNgbDateStructToDate(this.toDate), 
      this.convertNgbDateStructToDate(this.fromDate2), 
      this.convertNgbDateStructToDate(this.toDate2),
      this.selectedStore.storeGuid); 
  }

  isHovered = date => !this.isSelectingRange2 && this.fromDate && !this.toDate && this.hoveredDate && after(date, this.fromDate) && before(date, this.hoveredDate) && before(date, this.maxDate) && after(date, this.minDate);
  
  // Range 1
  isInside = date => this.fromDate && this.toDate && after(date, this.fromDate) && before(date, this.toDate);
  isFrom = date => this.fromDate && equals(date, this.fromDate);
  isTo = date => this.toDate && equals(date, this.toDate);

  // Range 2
  isInside2 = date => after(date, this.fromDate2) && before(date, this.toDate2);
  isFrom2 = date => equals(date, this.fromDate2);
  isTo2 = date => equals(date, this.toDate2);

  isInvalid = date => after(date, this.maxDate) || before(date, this.minDate);

  isRange2 = date => (
    this.isSelectingRange2 && 
    this.hoveredDate && 
    this.findNearestDate(this.hoveredDate) &&
    before(date, this.subtractDaysFromDate(this.nearestDate, -1)) && 
    after(date, this.subtractDaysFromDate(this.nearestDate, this.rangeLength+1))
  );

  lastDateCalculated: NgbDateStruct;
  nearestDate: NgbDateStruct;

  findNearestDate(date: NgbDateStruct): boolean {
    if(isNullOrUndefined(date) || this.lastDateCalculated == date) {
      return true;
    }
    let currDate: Date = this.convertNgbDateStructToDate(date);

    let targetDayofWeek = this.convertNgbDateStructToDate(this.toDate).getDay();

    let isUpPhase: boolean = true;
    let upCount: number = 1;

    while(currDate.getDay() != targetDayofWeek || 
      this.isWithinRange(currDate, this.fromDate, this.toDate) ||
      this.isWithinRange(new Date(currDate.getFullYear(), currDate.getMonth(), currDate.getDate() - this.rangeLength), this.fromDate, this.toDate)) {
        
      if(isUpPhase) {
        currDate = new Date(currDate.getFullYear(), currDate.getMonth(), currDate.getDate() + 1);
        upCount += 1;
        if(upCount > (this.rangeLength)) {
          isUpPhase = false;
        }
      }
      else {
        currDate = new Date(currDate.getFullYear(), currDate.getMonth(), currDate.getDate() - 1);
      }
      
    }

    this.lastDateCalculated = date;
    this.nearestDate = this.convertDateToNgbDateStruct(currDate);
    return true;
  }

  isWithinRange(date: Date, fromDate: NgbDateStruct, toDate: NgbDateStruct) {
    return date >= this.convertNgbDateStructToDate(fromDate) && date <= this.convertNgbDateStructToDate(toDate);
  }

  subtractDaysFromDate(date: NgbDateStruct, numDays: number): NgbDateStruct {
    let newDate: Date = new Date(date.year, date.month-1, date.day - numDays);
    return new NgbDate(newDate.getFullYear(), newDate.getMonth()+1, newDate.getDate());
  }

  ngOnInit() {
    // On init, check size of viewport and use the appropriately sized Calendar
    const initIsLargeScreen = this.breakpointObserver.isMatched(isLargeScreenMediaQuery);
    this.updateDatepickerSize(initIsLargeScreen);

    // After init, listen to changes in size of viewport
    // and resize to small or large Calendar when appropriate
    this.resizeListener = (isLargeScreenMatch: MediaQueryList) => {
      // Run the query "in the zone"
      // This is necessary for Angular to realize changes to this.currentCalendar
      this.zone.run(() => {
        this.updateDatepickerSize(isLargeScreenMatch.matches);
      })
    };

    this.matcher = this.mediaMatcher.matchMedia(isLargeScreenMediaQuery);
    this.matcher.addListener(this.resizeListener);
  }

  updateDatepickerSize(isLargeScreen: boolean) {
    if (isLargeScreen) {
      this.currentCalendar = this.largeCalendar;  
    }
    else {
      this.currentCalendar = this.smallCalendar;
    }
    //output
    this.isSmallDatepicker.emit(isLargeScreen);
  }

  ngOnDestroy() {
    this.matcher.removeListener(this.resizeListener);
  }
}

